import { createAsyncThunk } from '@reduxjs/toolkit';

import api from 'api';

export const SLICE_PREFIX = 'admin';

/** HEALTH SYSTEMS * */
export const listHealthSystems = createAsyncThunk(
  `${SLICE_PREFIX}/listHealthSystems`,
  async (
    { page, sortBy, sortOrder, pageSize, filters },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await api.getHealthSystems({
        page,
        sortBy,
        sortOrder,
        pageSize,
        filters,
      });

      return {
        data: data.healthSystems,
        pageCount: data.pageCount,
      };
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  },
);

/** NETWORKS * */
export const listNetworks = createAsyncThunk(
  `${SLICE_PREFIX}/listNetworks`,
  async ({ page, sortBy, sortOrder, pageSize }, { rejectWithValue }) => {
    try {
      const { data } = await api.getNetworks({
        page,
        sortBy,
        sortOrder,
        pageSize,
      });
      return {
        data: data.networks,
        pageCount: data.pageCount,
      };
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  },
);

/** USERS * */
export const listUsers = createAsyncThunk(
  `${SLICE_PREFIX}/listUsers`,
  async (
    { page, sortBy, sortOrder, pageSize, filters },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await api.getUsers({
        page,
        sortBy,
        sortOrder,
        pageSize,
        filters,
      });
      return {
        data: data.users,
        pageCount: data.pageCount,
      };
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const addUser = createAsyncThunk(
  `${SLICE_PREFIX}/addUser`,
  async (user, { rejectWithValue }) => {
    try {
      const { data } = await api.createUser(user);
      return data;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const getUserById = createAsyncThunk(
  `${SLICE_PREFIX}/getUserById`,
  async (userId, { rejectWithValue }) => {
    try {
      const {
        data: { userWithHealthSystemAssociationDto },
      } = await api.getUser(userId);
      return userWithHealthSystemAssociationDto;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const updateUser = createAsyncThunk(
  `${SLICE_PREFIX}/updateUser`,
  async (user, { rejectWithValue }) => {
    try {
      const {
        data: { userWithHealthSystemAssociationDto },
      } = await api.updateUser(user);
      return userWithHealthSystemAssociationDto;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const resetUserPassword = createAsyncThunk(
  `${SLICE_PREFIX}/resetUserPassword`,
  async (id, { rejectWithValue }) => {
    try {
      await api.resetUserPassword(id);
      return null;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const reInviteUser = createAsyncThunk(
  `${SLICE_PREFIX}/reInviteUser`,
  async (id, { rejectWithValue }) => {
    try {
      await api.reInviteUser(id);
      return null;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  },
);

/** FACILITIES * */

export const listFacilities = createAsyncThunk(
  `${SLICE_PREFIX}/listFacilities`,
  async (
    { page, sortBy, sortOrder, pageSize, filters },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await api.getFacilities({
        page,
        sortBy,
        sortOrder,
        pageSize,
        filters,
      });
      return {
        data: data.facilities,
        pageCount: data.pageCount,
      };
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  },
);

/* CONSULTS */
export const listConsults = createAsyncThunk(
  `${SLICE_PREFIX}/listConsults`,
  async ({ service, ...args }, { rejectWithValue }) => {
    try {
      const { data } = await api.getConsults(service, args);
      return {
        data: data.consults,
        pageCount: data.pageCount,
        totalCount: data.totalCount,
      };
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchConsult = createAsyncThunk(
  `${SLICE_PREFIX}/fetchConsult`,
  async (consultId, { rejectWithValue }) => {
    try {
      const { data } = await api.getConsultById(consultId);
      return { ...data.consult, id: String(consultId) };
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const updateAdminConsult = createAsyncThunk(
  `${SLICE_PREFIX}/updateAdminConsult`,
  async ({ consultId, data }, { rejectWithValue }) => {
    try {
      await api.updateFullConsult(consultId, data);
      const { data: updatedData } = await api.getConsultById(consultId);
      return { ...updatedData.consult, id: String(consultId) };
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const linkOrder = createAsyncThunk(
  `${SLICE_PREFIX}/linkOrder`,
  async (linkData, { rejectWithValue }) => {
    try {
      const { data } = await api.linkOrder(linkData);
      return data;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const unlinkOrder = createAsyncThunk(
  `${SLICE_PREFIX}/unlinkOrder`,
  async ({ consultId }, { rejectWithValue }) => {
    try {
      const { data } = await api.unlinkOrder({ consultId });
      return data;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  },
);

/** HL7 PATIENTS * */
export const listHl7Patients = createAsyncThunk(
  `${SLICE_PREFIX}/listHl7Patients`,
  async (
    { page, sortBy, sortOrder, pageSize, filters },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await api.getHl7Patients({
        page,
        sortBy,
        sortOrder,
        pageSize,
        filters,
      });
      return {
        data: data.data,
        pageCount: data.pageCount,
        count: data.count,
      };
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const getHl7PatientById = createAsyncThunk(
  `${SLICE_PREFIX}/getHl7PatientById`,
  async (hl7PatientId, { rejectWithValue }) => {
    try {
      const { data } = await api.getHl7Patient(hl7PatientId);
      return data;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const createHl7Patient = createAsyncThunk(
  `${SLICE_PREFIX}/createHl7Patient`,
  async (patient, { rejectWithValue }) => {
    try {
      const { data } = await api.createHl7Patient(patient);
      return data;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const updateHl7Patient = createAsyncThunk(
  `${SLICE_PREFIX}/updateHl7Patient`,
  async (patient, { rejectWithValue }) => {
    try {
      const { data } = await api.updateHl7Patient(patient);
      return data;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  },
);

/** HL7 VISITS * */
export const listHl7Visits = createAsyncThunk(
  `${SLICE_PREFIX}/listHl7Visits`,
  async (
    { page, sortBy, sortOrder, pageSize, filters },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await api.getHl7Visits({
        page,
        sortBy,
        sortOrder,
        pageSize,
        filters,
      });
      return {
        data: data.data,
        pageCount: data.pageCount,
        count: data.count,
      };
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const getHl7VisitById = createAsyncThunk(
  `${SLICE_PREFIX}/getHl7VisitById`,
  async (hl7VisitId, { rejectWithValue }) => {
    try {
      const { data } = await api.getHl7Visit(hl7VisitId);
      return data;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const createHl7Visit = createAsyncThunk(
  `${SLICE_PREFIX}/createHl7Visit`,
  async (visit, { rejectWithValue }) => {
    try {
      const { data } = await api.createHl7Visit(visit);
      return data;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const updateHl7Visit = createAsyncThunk(
  `${SLICE_PREFIX}/updateHl7Visit`,
  async (visit, { rejectWithValue }) => {
    try {
      const { data } = await api.updateHl7Visit(visit);
      return data;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const addHl7VisitInsurance = createAsyncThunk(
  `${SLICE_PREFIX}/addHl7VisitInsurance`,
  async (hl7VisitId, { rejectWithValue }) => {
    try {
      const { data } = await api.addHl7VisitInsurance(hl7VisitId);
      return data;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  },
);

/** HL7 ORDERS * */
export const listHl7Orders = createAsyncThunk(
  `${SLICE_PREFIX}/listHl7Orders`,
  async (
    { page, sortBy, sortOrder, pageSize, filters },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await api.getHl7Orders({
        page,
        sortBy,
        sortOrder,
        pageSize,
        filters,
      });
      return {
        data: data.data,
        pageCount: data.pageCount,
        count: data.count,
      };
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const getHl7OrderById = createAsyncThunk(
  `${SLICE_PREFIX}/getHl7OrderById`,
  async (hl7OrderId, { rejectWithValue }) => {
    try {
      const { data } = await api.getHl7Order(hl7OrderId);
      return data;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const createHl7Order = createAsyncThunk(
  `${SLICE_PREFIX}/createHl7Order`,
  async (order, { rejectWithValue }) => {
    try {
      const { data } = await api.createHl7Order(order);
      return data;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const updateHl7Order = createAsyncThunk(
  `${SLICE_PREFIX}/updateHl7Order`,
  async (order, { rejectWithValue }) => {
    try {
      const { data } = await api.updateHl7Order(order);
      return data;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  },
);
